import type * as Y from 'yjs';

import {
  type BasicYMap,
  type DisposableElement,
  type WrappedYType,
  isDisposableElement,
  isWrappedYType
} from './types';
import { registerIntegrateHandler, unregisterIntegrateHandler } from './utils';

export class SynchronizedMap<T> implements DisposableElement, WrappedYType {
  #data: Record<string, T> = {};

  constructor(
    private map: BasicYMap<unknown>,
    private mapper: (input: unknown, key: string) => T | undefined
  ) {
    this.initializeData();
    this.map.observe(this.onChanges);

    const unwrapped = this.unwrap();
    if (unwrapped) {
      registerIntegrateHandler(unwrapped, this.onIntegrate);
    }
  }

  public set(key: string, value: T): void {
    this.map.set(key, this.unmapValue(value));
  }

  public delete(key: string): void {
    this.map.delete(key);
    const data = this.#data[key];
    if (data && isDisposableElement(data)) {
      data.dispose();
    }

    delete this.#data[key];
  }

  private initializeData() {
    this.#data = {};
    this.map.forEach((value, key) => {
      const plainValue = this.mapper(value, key);

      if (plainValue) {
        this.#data[key] = plainValue;
      }
    });
  }

  private onIntegrate = () => {
    this.initializeData();
  };

  private onChanges = (changes: Y.YMapEvent<any>) => {
    for (const [key, { action }] of changes.keys) {
      switch (action) {
        case 'add': {
          const value = this.mapper(this.map.get(key), key);

          if (value) {
            this.#data[key] = value;
          }

          break;
        }
        case 'update': {
          const value = this.mapper(this.map.get(key), key);

          const oldValue = this.#data[key];
          if (isDisposableElement(oldValue)) {
            oldValue.dispose();
          }

          if (value) {
            this.#data[key] = value;
          } else {
            delete this.#data[key];
          }

          break;
        }
        case 'delete': {
          const value = this.#data[key];
          if (isDisposableElement(value)) {
            value.dispose();
          }
          delete this.#data[key];

          break;
        }
      }
    }
  };

  get data(): Record<string, T> {
    return this.#data;
  }

  private unmapValue(val: T): unknown {
    if (isWrappedYType(val)) {
      return val.unwrap();
    } else {
      return val;
    }
  }

  observeDeep(cb: () => void): void {
    this.map.observeDeep(cb);
  }

  unobserveDeep(cb: () => void): void {
    this.map.unobserveDeep(cb);
  }

  unwrap(): Y.AbstractType<any> | undefined {
    return isWrappedYType(this.map) ? this.map.unwrap() : (this.map as Y.Map<unknown>);
  }

  dispose(): void {
    this.map.unobserve(this.onChanges);

    const unwrapped = this.unwrap();
    if (unwrapped) {
      unregisterIntegrateHandler(unwrapped, this.onIntegrate);
    }

    for (const value of Object.values(this.#data)) {
      if (isDisposableElement(value)) {
        value.dispose();
      }
    }
  }
}
